import { useMemo } from "react";

import useCompanyAutocompleteListQuery from "app/pages/sales-intelligence/modules/company-autocomplete/queries/useCompanyAutocompleteListQuery";
import salesSettingsHelper from "app/pages/sales-intelligence/services/salesSettingsHelper";
import { RecentService } from "app/services/recent/recentService";
import type { IAutoCompleteSiteItem } from "app/@types/autocomplete";
import type { IRecentObject } from "app/@types/userdata";

const filterUniqueCompanies = (items?: IAutoCompleteSiteItem[], maxCount = 8) => {
    if (!items) {
        return [];
    }
    const seen = new Set<string>();
    const result: IAutoCompleteSiteItem[] = [];
    for (const item of items) {
        const mainDomain = item.companyInfo?.mainDomain;
        if (!mainDomain || seen.has(mainDomain)) {
            continue;
        }
        seen.add(mainDomain);
        result.push(item);
        if (result.length >= maxCount) {
            break;
        }
    }
    return result;
};

export type EnrichedRecent = IRecentObject & {
    companyData: IAutoCompleteSiteItem;
};

type EnrichedRecentReturn = {
    enrichedRecents: EnrichedRecent[];
    recentUniqueCompanies: IAutoCompleteSiteItem[];
    isPending: boolean;
};

const RECENT_WEBSITES_LIMIT = 16;

const useEnrichedRecents = (limit = RECENT_WEBSITES_LIMIT): EnrichedRecentReturn => {
    const recents = RecentService.getRecentsWebsitesAndCompaniesByType(limit);
    const isDataCreditsEnabled = salesSettingsHelper.isDataCreditsEnabled;

    const { data: companiesData, isPending } = useCompanyAutocompleteListQuery(
        recents.map((recent) => recent.data.mainItem),
        isDataCreditsEnabled,
    );

    const enrichedRecents: EnrichedRecent[] = useMemo(() => {
        return recents
            .map((recent) => {
                const itemData = companiesData?.find((item) => item.name === recent.data.mainItem);
                if (!itemData || !itemData.hasData) {
                    return null;
                }
                return {
                    ...recent,
                    companyData: itemData,
                };
            })
            .filter(Boolean) as EnrichedRecent[];
    }, [recents, companiesData]);

    const recentUniqueCompanies = useMemo(
        () => filterUniqueCompanies(enrichedRecents.map((recent) => recent.companyData)),
        [enrichedRecents],
    );

    return { enrichedRecents, recentUniqueCompanies, isPending };
};

export default useEnrichedRecents;
